import { z } from 'zod'
import { Id, Timestamp } from './common'

export const TorqueSpec = z.object({
  id: Id,
  component_id: Id,
  location: z.string(),
  /** Newton-metres (SI). */
  torque_nm: z.number(),
  note: z.string().nullable(),
  created_at: Timestamp,
})
export type TorqueSpec = z.infer<typeof TorqueSpec>

export const TorqueSpecList = z.object({ specs: z.array(TorqueSpec) })

export const CreateTorqueBody = z.object({
  location: z.string().trim().min(1, 'Say which bolt, e.g. Stem faceplate.').max(120),
  torque_nm: z.number().positive().max(200),
  note: z
    .string()
    .trim()
    .max(200)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
})
export type CreateTorqueBody = z.input<typeof CreateTorqueBody>

export const ComponentTorqueParams = z.object({ id: Id, specId: Id })

const Step = z.string().trim().min(1).max(200)

export const Checklist = z.object({
  id: Id,
  kind: z.enum(['pre_ride', 'general']),
  title: z.string(),
  steps: z.array(z.string()),
  updated_at: Timestamp,
})
export type Checklist = z.infer<typeof Checklist>

export const ChecklistList = z.object({ checklists: z.array(Checklist) })

export const CreateChecklistBody = z.object({
  title: z.string().trim().min(1).max(120),
  steps: z.array(Step).min(1).max(50),
})
export type CreateChecklistBody = z.input<typeof CreateChecklistBody>

export const UpdateChecklistBody = CreateChecklistBody.partial().refine(
  (b) => Object.keys(b).length > 0,
  'Nothing to update.',
)

export const ChecklistRun = z.object({
  id: Id,
  checklist_id: Id,
  bike_id: Id,
  completed_at: Timestamp,
  skipped: z.array(z.string()),
})
export type ChecklistRun = z.infer<typeof ChecklistRun>

export const CreateChecklistRunBody = z.object({
  checklist_id: Id,
  /** Steps that were not ticked (by text). */
  skipped: z.array(Step).max(50).default([]),
})
export type CreateChecklistRunBody = z.input<typeof CreateChecklistRunBody>

export const JobStep = z.object({ text: Step, done: z.boolean().default(false) })
export type JobStep = z.infer<typeof JobStep>

export const SetJobStepsBody = z.object({ steps: z.array(JobStep).max(100) })
export type SetJobStepsBody = z.input<typeof SetJobStepsBody>

/**
 * The starter pre-ride check (an "M-check": front wheel up to the bars,
 * down the frame and back along the drivetrain). Riders can edit it.
 */
export const DEFAULT_PRE_RIDE_STEPS = [
  'Tyres: pressures set, no cuts or bulges',
  'Front wheel: axle tight, no play, rim runs true',
  'Front brake: lever firm, pads have life left',
  'Headset: no knock with the front brake on',
  'Cockpit: bars and stem tight and lined up',
  'Saddle and seatpost tight; dropper goes up and down',
  'Cranks and pedals tight, no play in the bottom bracket',
  'Chain clean and lubed; gears shift cleanly',
  'Rear wheel: axle tight, no play, rim runs true',
  'Rear brake: lever firm, pads have life left',
  'Suspension: sag right, stanchions clean, no oil leaks',
]
